import { Button } from "@progress/kendo-react-buttons";
import { Checkbox, ColorPicker, Input } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { TreeView } from "@progress/kendo-react-treeview";
import { useEffect, useState } from "react";
import { CodeEditor } from "../../CodeEditor/CodeEditor.tsx";
import { templateData } from "../../Template/NewTemplateSignals";
import "../ExpressionBuilder/ExpressionBuilder.css";
import { componentTree } from "../ExpressionTrees/ComponentTree";
import { definitionsTree } from "../ExpressionTrees/DefinitionTree";
import { masterTree } from "../ExpressionTrees/MasterTree";
import { ColorChangingIcon } from "../UI/ColorChangingIcon";

const CustomItemRender = (props, setInternalExpression, editor) => {
  const onChildDoubleClick = (item, setInternalExpression) => {
    setInternalExpression((prevState) => {
      if (prevState === undefined) {
        return item.expressionText;
      }
      return prevState + " " + item.expressionText;
    });
    const insertTextAtCursor = (text) => {
      if (!editor) return;
      const selection = editor.getSelection();
      if (selection) {
        editor.executeEdits("external", [
          {
            range: selection,
            text: text,
            forceMoveMarkers: true,
          },
        ]);
      } else {
        const position = editor.getPosition() || { lineNumber: 1, column: 1 };
        editor.executeEdits("external", [
          {
            range: {
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            },
            text: text,
            forceMoveMarkers: true,
          },
        ]);
      }
      editor.focus();
    };
    insertTextAtCursor(item.expressionText);
  };
  return (
    <div className="align-columns">
      {props.item.fontawesomeIcon && (
        <div style={{ width: "20px", justifyContent: "center", display: "flex" }}>
          <ColorChangingIcon
            icon={props.item.fontawesomeIcon}
            initialPrimaryColor={props.item.initialPrimaryColor || "#605e5c"}
            initialSecondaryColor={props.item.initialSecondaryColor || "#0078D4"}
            initialSize={16}
            hoverSize={16}
          />
        </div>
      )}
      <div onDoubleClick={() => onChildDoubleClick(props.item, setInternalExpression)}>
        {props.item.text}
      </div>
    </div>
  );
};

function ExpressionFieldsHelper() {
  let fieldNamesTemp = [];
  templateData.value?.sections.forEach((section) => {
    section.fields.forEach((field) => {
      fieldNamesTemp.push({
        text: field.fieldName,
        math: "Fields",
        expressionText: field.fieldName?.replace(/\s/g, ""),
        fieldType: field.fieldType,
      });
    });
  });
  return fieldNamesTemp;
}

const ConditionRuleEditor = ({ rule, onClose, dataState, setDataState }) => {
  const [backgroundColor, setBackgroundColor] = useState(rule.style?.backgroundColor || "white");
  const [textColor, setTextColor] = useState(rule.style?.color || "black");
  const [stopProcessing, setStopProcessing] = useState(rule.stopProcessing || false);
  const [selectedComponent, setSelectedComponent] = useState("");
  const [functionDefinition, setFunctionDefinition] = useState("");
  const [masterTreeData, setMasterTreeData] = useState(masterTree);
  const [componentTreeData, setComponentTreeData] = useState(componentTree);
  const [definitionsTreeData] = useState(definitionsTree);
  const fields = ExpressionFieldsHelper();
  const [internalExpression, setInternalExpression] = useState(rule.rule || "");
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    setBackgroundColor(rule.style?.backgroundColor || "white");
    setTextColor(rule.style?.color || "black");
    setInternalExpression(rule.rule || "");
    setStopProcessing(rule.stopProcessing || false);
  }, [rule]);

  useEffect(() => {
    if (componentTreeData) {
      if (componentTreeData.some((x) => x.text === "Fields")) {
        return;
      }
      setComponentTreeData((prevState) => {
        const fieldsData = { text: "Fields", items: fields };
        return [...prevState, fieldsData];
      });
    }
  }, [fields]);

  const handleMasterClick = (e) => {
    if (e.item.isParent) {
      setMasterTreeData((prevState) => {
        const newState = [...prevState];
        newState.forEach((item) => {
          if (item.text === e.item.text) {
            item.expanded = !item.expanded;
            item.isSelected = !item.isSelected;
          }
        });
        return newState;
      });
    } else {
      setSelectedComponent(e.item.text);
      setMasterTreeData((prevState) => {
        const newState = [...prevState];
        newState.forEach((item) => {
          if (item.text === e.item.text) {
            item.isSelected = !item.isSelected;
          }
        });
        return newState;
      });
    }
  };

  const handleChildClick = (e) => {
    setFunctionDefinition(definitionsTreeData.find((x) => x.math === e.item.math)?.text);
  };

  const handleSubmit = () => {
    const updatedDataState = dataState.map((ruleState) => {
      if (ruleState.id === rule.id) {
        return {
          ...ruleState,
          rule: internalExpression,
          stopProcessing: stopProcessing,
          style: { backgroundColor, color: textColor },
        };
      }
      return ruleState;
    });
    setDataState(updatedDataState);
    onClose();
  };

  return (
    <div className="expression-page-content">
      <div style={{ display: "flex", width: "100%", alignItems: "flex-start" }}>
        <div
          className="expression-container"
          style={{ flex: 1, minWidth: 0, flexDirection: "column", display: "flex" }}
        >
          <Label style={{ marginBottom: 4, fontWeight: 600 }}>Expression:</Label>
          <CodeEditor
            fields={fields}
            globalParameters={[]}
            internalExpression={internalExpression}
            setInternalExpression={setInternalExpression}
            setEditor={setEditor}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            marginLeft: "2rem",
            marginTop: "1.5rem",
            minWidth: 260,
            maxWidth: 320,
            alignItems: "flex-start",
          }}
        >
          <div
            className="align-columns"
            style={{ width: "100%" }}
          >
            <Label style={{ width: "40%" }}>Background:</Label>
            <ColorPicker
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.value)}
              views={["gradient"]}
            />
          </div>
          <div
            className="align-columns"
            style={{ width: "100%" }}
          >
            <Label style={{ width: "40%" }}>Text Color:</Label>
            <ColorPicker
              value={textColor}
              onChange={(e) => setTextColor(e.value)}
              views={["gradient"]}
            />
          </div>
          <div
            className="align-columns"
            style={{ width: "100%" }}
          >
            <Label style={{ width: "40%" }}>Preview:</Label>
            <Input
              value={"AaBbCcYyZz"}
              className="preview-input"
              style={{ backgroundColor: backgroundColor, color: textColor, width: "100%" }}
              readOnly
            />
          </div>
          <div
            className="align-columns"
            style={{ width: "100%" }}
          >
            <Checkbox
              checked={stopProcessing}
              label="Stop if True"
              onChange={() => {
                const newValue = !stopProcessing;
                setStopProcessing(newValue);
              }}
            />
          </div>
        </div>
      </div>
      <div className="component-container">
        <div className="component">
          <div className="parent-components-container">
            <TreeView
              className="parent-tree"
              data={masterTreeData}
              onItemClick={(e) => handleMasterClick(e)}
              expandField="expanded"
              expandIcons={true}
              scrollable={true}
              item={(props) => CustomItemRender(props, setInternalExpression, editor)}
            />
          </div>
        </div>
        <div className="component">
          <TreeView
            data={componentTreeData
              .filter((x) => x.text === selectedComponent)
              .flatMap((x) => x.items)
              .map((x) => ({
                text: x.text,
                math: x.math,
                expressionText: x.expressionText,
                parameterValue: x.parameterValue,
              }))}
            onItemClick={(e) => handleChildClick(e)}
            item={(props) => CustomItemRender(props, setInternalExpression, editor)}
          />
        </div>
        <div className="component">{functionDefinition}</div>
      </div>
      <div className="expression-builder-buttons">
        <Button
          themeColor={"success"}
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
          onClick={handleSubmit}
        >
          Save
        </Button>
        <Button
          themeColor={"info"}
          onClick={onClose}
          className="k-ml-3 k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ConditionRuleEditor;
